import * as React from 'react';
import { toast } from 'sonner';
import { useNavigate, useRouteContext } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { COUNTIES_BY_VOIVODESHIP, VOIVODESHIPS, mediaTypeRequiresDimensions } from '@ooh/contracts';
import { suppliersQuery, formatsQuery, type Supplier, type MediaFormat } from '../api/queries';
import { multipartFetch } from '../api/multipart-fetch';
import { createFormSchema, type FormValues } from '../../../lib/media-unit-form-schema';
import { computedArea, buildCreateDto } from '../lib/form-helpers';
import { partitionPhotoSelection } from '@ooh/screens';
import type { SelectedPhoto } from '@ooh/screens';

/**
 * Shared with the post-save "add another" reset (`handleAddAnother` below) so both the
 * initial mount and the reset-to-add-mode path stay in sync with a single source of truth.
 */
const DEFAULT_FORM_VALUES: FormValues = {
  supplierNumber: '',
  supplierId: '',
  formatId: '',
  regionCode: '',
  countyName: '',
  agglomeration: '',
  city: '',
  address: '',
  widthM: '',
  heightM: '',
  material: '',
  lighting: '',
  latitude: '',
  longitude: '',
  notes: '',
  wfContactPerson: '',
  locationDescription: '',
  catalogPrice: '',
  rent: '',
  productionCost: '',
  installCost: '',
  dismantlingCost: '',
  tagDefinitionIds: [],
};

// eslint-disable-next-line max-lines-per-function
export function useAddForm(addToOfferId?: string) {
  const { t } = useTranslation('inventory');
  const navigate = useNavigate();
  const { auth } = useRouteContext({ from: '__root__' });
  // Forwarded on every navigate back to `/media-units` so the offer-add context
  // survives creating a unit and returning to the catalog (CU-86caypxaw).
  const catalogSearch = React.useMemo(
    () => (addToOfferId ? { addToOfferId } : undefined),
    [addToOfferId],
  );

  const [photos, setPhotos] = React.useState<SelectedPhoto[]>([]);
  const photosRef = React.useRef<SelectedPhoto[]>([]);
  const [photoCountError, setPhotoCountError] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Matches buildCreateDto's hardcoded billboard variant (no media-type selector yet).
  const formSchema = React.useMemo(() => createFormSchema(t, true, 'billboard'), [t]);
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: DEFAULT_FORM_VALUES,
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = form;

  const regionCode = watch('regionCode');
  const area = computedArea(watch('widthM'), watch('heightM'));
  const filteredCounties = regionCode ? (COUNTIES_BY_VOIVODESHIP[regionCode] ?? []) : [];

  const handleRegionChange = React.useCallback(
    (value: string) => {
      setValue('regionCode', value);
      setValue('countyName', '');
    },
    [setValue],
  );

  const { data: suppliersData, isLoading: isSuppliersLoading } = useQuery(suppliersQuery(auth));
  const { data: formatsData, isLoading: isFormatsLoading } = useQuery(formatsQuery(auth));
  // suppliersQuery returns SupplierListItem[] (plain array — no .data wrapper)
  const suppliers: Supplier[] = suppliersData ?? [];
  const formats: MediaFormat[] = (formatsData as { data?: MediaFormat[] })?.data ?? [];
  const supplierOptions = suppliers.map((s) => ({ value: String(s.id), label: s.name }));
  const formatOptions = formats.map((f) => ({ value: String(f.id), label: f.label }));

  const handleFileChange = React.useCallback(
    (files: FileList | File[]) => {
      const allFiles = Array.from(files);
      const { accepted, error } = partitionPhotoSelection({
        selectedFiles: allFiles,
        currentCount: photos.length,
        t,
      });
      setPhotoCountError(error);
      if (accepted.length > 0) {
        setPhotos((prev) => [
          ...prev,
          ...accepted.map((file) => ({
            id: crypto.randomUUID(),
            file,
            url: URL.createObjectURL(file),
          })),
        ]);
      }
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [photos.length, t],
  );

  const handleRemovePhoto = React.useCallback((index: number) => {
    setPhotos((prev) => {
      const updated = [...prev];
      const removed = updated.splice(index, 1)[0];
      if (removed) URL.revokeObjectURL(removed.url);
      return updated;
    });
  }, []);

  React.useEffect(() => {
    photosRef.current = photos;
  }, [photos]);
  React.useEffect(() => {
    return () => {
      photosRef.current.forEach((p) => URL.revokeObjectURL(p.url));
    };
  }, []);

  const [isSuccess, setIsSuccess] = React.useState(false);
  // Tracks the pending auto-redirect-to-catalog timeout so `handleAddAnother` can cancel it
  // instead of racing a stale navigate after the user chose to stay on the form.
  const successTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    return () => {
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    };
  }, []);

  const { mutate: createMediaUnit, isPending } = useMutation({
    mutationFn: async (values: FormValues) => {
      const dto = buildCreateDto(values);
      const formData = new FormData();
      formData.append('data', JSON.stringify(dto));
      photosRef.current.forEach((p) => formData.append('photos', p.file));
      const token = auth.accessToken;
      const response = await multipartFetch('/media-units', 'POST', formData, token);
      if (!response.ok) {
        const body: unknown = await response.json().catch(() => ({}));
        throw new Error((body as { message?: string }).message ?? `HTTP ${response.status}`);
      }
      return response.json() as Promise<unknown>;
    },
    onSuccess: () => {
      setIsSuccess(true);
      toast.success(t('form.successAdd.title'));
      successTimeoutRef.current = setTimeout(() => {
        successTimeoutRef.current = null;
        void navigate({ to: '/media-units', search: catalogSearch });
      }, 1500);
    },
    onError: (err: Error) => {
      toast.error(err.message ?? t('form.saveError'));
    },
  });

  const onSubmit = handleSubmit((values) => {
    createMediaUnit(values);
  });

  // Cancels the pending auto-redirect and resets the form/photos to a clean add-mode state,
  // so the user can add another media unit without navigating back through the catalog list.
  const handleAddAnother = React.useCallback(() => {
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = null;
    }
    photosRef.current.forEach((p) => URL.revokeObjectURL(p.url));
    setPhotos([]);
    setPhotoCountError(null);
    form.reset(DEFAULT_FORM_VALUES);
    setIsSuccess(false);
  }, [form]);

  // Cancels the pending auto-redirect and navigates immediately, so a deliberate click
  // doesn't race the same navigate firing again from the still-pending timeout.
  const handleGoToCatalogNow = React.useCallback(() => {
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = null;
    }
    void navigate({ to: '/media-units', search: catalogSearch });
  }, [navigate, catalogSearch]);

  return {
    t,
    register,
    watch,
    setValue,
    errors,
    onSubmit,
    isPending,
    isSuccess,
    photos,
    fileInputRef,
    photoCountError,
    handleFileChange,
    handleRemovePhoto,
    handleAddAnother,
    handleGoToCatalogNow,
    // Exported so the component reuses the same derivation for its own
    // back-link/cancel navigate calls instead of re-deriving it (CU-86caypxaw).
    catalogSearch,
    supplierOptions,
    formatOptions,
    isSuppliersLoading,
    isFormatsLoading,
    filteredCounties,
    handleRegionChange,
    area,
    voivodeshipOptions: VOIVODESHIPS,
    navigate,
    // Matches buildCreateDto's hardcoded billboard variant (no media-type
    // selector yet); derived the same way createFormSchema derives it internally.
    isDimensionRequired: mediaTypeRequiresDimensions('billboard'),
  };
}
